import { isNode, LineCounter, parseDocument, stringify } from 'yaml';
import { CONFIG_VERSION_V2, normalizeHolds, ProjectConfig, V2_MACHINE_MESSAGE, } from './schema.js';
const HOLD_DEPRECATION_WARNING = '`hold:` is deprecated; use `resources.held` (same fields). It is merged into `resources.held` at parse time.';
export const CONFIG_FILENAME = 'viko.project.yaml';
export function formatPath(path) {
    let out = '';
    for (const seg of path) {
        if (typeof seg === 'number')
            out += `[${seg}]`;
        else
            out += out === '' ? String(seg) : `.${String(seg)}`;
    }
    return out;
}
/** Human-readable multi-line error message: `file:line:col path: message`. */
export function formatIssues(issues, filename = CONFIG_FILENAME) {
    return issues
        .map((i) => {
        const loc = i.line !== undefined ? `${filename}:${i.line}:${i.col ?? 1}` : filename;
        return `${loc} ${i.path === '' ? '(root)' : i.path}: ${i.message}`;
    })
        .join('\n');
}
export class ProjectConfigError extends Error {
    issues;
    constructor(issues, filename = CONFIG_FILENAME) {
        super(`Invalid ${filename}:\n${formatIssues(issues, filename)}`);
        this.issues = issues;
        this.name = 'ProjectConfigError';
    }
}
function zodIssueMessage(issue) {
    if (issue.code === 'unrecognized_keys') {
        return `unknown key${issue.keys.length > 1 ? 's' : ''} ${issue.keys.map((k) => `"${k}"`).join(', ')}`;
    }
    if (issue.code === 'invalid_union') {
        return `${issue.message}; see the JSON Schema for accepted shapes`;
    }
    return issue.message;
}
/** v1 is deprecated: it still parses, with this warning. */
export const V1_DEPRECATION_WARNING = 'config v1: machine names move to machine-local policy (migrate to version: 2)';
/**
 * Machine-local keys a v2 file must not contain, as paths into the raw document. Empty maps
 * (`machines: { allowed: [] }`, `paths: {}`, `roots: {}`) name no machine, so a normalized v2
 * config re-validates.
 */
export function v2MachineKeyPaths(raw) {
    const out = [];
    if (!raw || typeof raw !== 'object')
        return out;
    const r = raw;
    const obj = (v) => (v && typeof v === 'object' ? v : {});
    const arr = (v) => (Array.isArray(v) ? v : []);
    const named = (v) => v !== undefined && !(v && typeof v === 'object' && Object.keys(v).length === 0);
    if ('machines' in r && (typeof r.machines !== 'object' || arr(obj(r.machines).allowed).length))
        out.push(['machines']);
    arr(r.repos).forEach((x, i) => {
        if (named(obj(x).paths))
            out.push(['repos', i, 'paths']);
    });
    if (named(obj(r.worktree).roots))
        out.push(['worktree', 'roots']);
    if (obj(r.landing).machine !== undefined)
        out.push(['landing', 'machine']);
    const pinned = (list, path) => arr(list).forEach((x, i) => {
        if (obj(x).machine !== undefined)
            out.push([...path, i, 'machine']);
    });
    pinned(r.releaseLanes, ['releaseLanes']);
    pinned(obj(r.secrets).commands, ['secrets', 'commands']);
    pinned(obj(r.doctor).custom, ['doctor', 'custom']);
    pinned(obj(r.routing).hints, ['routing', 'hints']);
    return out;
}
/**
 * v2: reject machine-local keys; the normalized config carries an empty `machines.allowed`
 * (any machine with a checkout), never the v1 `mac` default.
 */
function prepare(input) {
    const raw = input && typeof input === 'object' ? input : undefined;
    if (raw?.version !== CONFIG_VERSION_V2)
        return { data: input, errors: [], v1: !!raw };
    const errors = v2MachineKeyPaths(raw).map((p) => ({
        path: formatPath(p),
        message: V2_MACHINE_MESSAGE,
    }));
    return { data: { ...raw, machines: { allowed: [] } }, errors, v1: false };
}
/** Validate an already-parsed object (e.g. from JSON). */
export function validateProjectConfig(input) {
    const pre = prepare(input);
    if (pre.errors.length)
        return { ok: false, errors: pre.errors };
    const res = ProjectConfig.safeParse(pre.data);
    if (res.success)
        return {
            ok: true,
            config: normalizeHolds(res.data),
            warnings: [
                ...(pre.v1 ? [{ path: 'version', message: V1_DEPRECATION_WARNING }] : []),
                ...(res.data.hold?.length ? [{ path: 'hold', message: HOLD_DEPRECATION_WARNING }] : []),
            ],
        };
    return {
        ok: false,
        errors: res.error.issues.map((i) => ({
            path: formatPath(i.path),
            message: zodIssueMessage(i),
        })),
    };
}
/** Parse `viko.project.yaml` text; issues carry line/column when resolvable. */
export function parseProjectConfig(text) {
    const lineCounter = new LineCounter();
    const doc = parseDocument(text, { lineCounter, prettyErrors: false, uniqueKeys: true });
    if (doc.errors.length > 0) {
        return {
            ok: false,
            errors: doc.errors.map((e) => {
                const pos = lineCounter.linePos(e.pos[0]);
                return {
                    path: '',
                    message: `YAML syntax: ${e.message.split('\n')[0]}`,
                    line: pos.line,
                    col: pos.col,
                };
            }),
        };
    }
    const warnings = doc.warnings.map((w) => {
        const pos = lineCounter.linePos(w.pos[0]);
        return { path: '', message: w.message, line: pos.line, col: pos.col };
    });
    const pre = prepare(doc.toJS() ?? {});
    const locate = (path, out) => {
        // Walk up the path until a node with a source range is found.
        for (let n = path.length; n >= 0; n--) {
            const node = n === 0 ? doc.contents : doc.getIn(path.slice(0, n), true);
            if (isNode(node) && node.range) {
                const pos = lineCounter.linePos(node.range[0]);
                out.line = pos.line;
                out.col = pos.col;
                break;
            }
        }
        return out;
    };
    if (pre.errors.length) {
        const paths = v2MachineKeyPaths(doc.toJS());
        return {
            ok: false,
            errors: pre.errors.map((e, i) => locate(paths[i] ?? [], { ...e })),
        };
    }
    const res = ProjectConfig.safeParse(pre.data);
    if (res.success) {
        if (pre.v1)
            warnings.push({ path: 'version', message: V1_DEPRECATION_WARNING });
        if (res.data.hold?.length)
            warnings.push(locate(['hold'], { path: 'hold', message: HOLD_DEPRECATION_WARNING }));
        return { ok: true, config: normalizeHolds(res.data), warnings };
    }
    return {
        ok: false,
        errors: res.error.issues.map((issue) => locate(issue.path, { path: formatPath(issue.path), message: zodIssueMessage(issue) })),
    };
}
/** Parse or throw `ProjectConfigError`. */
export function loadProjectConfig(text, filename = CONFIG_FILENAME) {
    const res = parseProjectConfig(text);
    if (!res.ok)
        throw new ProjectConfigError(res.errors, filename);
    return res.config;
}
/** Serialize a config (input or normalized) to YAML with an optional header comment. */
export function stringifyProjectConfig(config, header) {
    const body = stringify(config, { lineWidth: 100 });
    if (!header)
        return body;
    const comment = header
        .split('\n')
        .map((l) => (l === '' ? '#' : `# ${l}`))
        .join('\n');
    return `${comment}\n${body}`;
}
//# sourceMappingURL=parse.js.map